import mBaseModel                 from '@/models/mBaseModel';
import UtilsData                  from '@/utilities/UtilsData';
import mReportParameter           from '@/models/mReportParameter';
import mReportColumn              from '@/models/mReportColumn';
import mDealCirtLiabilityStepDown from '@/models/mDealCirtLiabilityStepDown';
import formatter                  from '@/utilities/Formatter';
import moment                     from 'moment';

export default class mDealCirt extends mBaseModel {
  static DefaultLiabilityStepDownList() {
    return [
      { StartPeriod: 12, EndPeriod: 1, Reassess: 0, BalanceMultiplier: 1.15, DelinquentMultiplier: 6.5 },
      { StartPeriod: 24, EndPeriod: 1, Reassess: 0, BalanceMultiplier: 1, DelinquentMultiplier: 4.25 },
      { StartPeriod: 36, EndPeriod: 1, Reassess: 0, BalanceMultiplier: 1, DelinquentMultiplier: 3 },
      { StartPeriod: 48, EndPeriod: 1, Reassess: 0, BalanceMultiplier: 1, DelinquentMultiplier: 3 },
      { StartPeriod: 60, EndPeriod: 1, Reassess: 0, BalanceMultiplier: 1, DelinquentMultiplier: 2 },
      { StartPeriod: 999, EndPeriod: 1, Reassess: 0, BalanceMultiplier: 1, DelinquentMultiplier: 2 },
    ]
  }

  static Defaults = () => {
    const now = new Date();
    return Object.freeze({
      ReferencePoolBalance: 1000000000.0,
      Wac: 3.0,
      Wam: 357,
      AnnualPremiumRatePct: 0.12,
      RetentionPct: 0.50,
      LimitOfLiabilityPct: 3.50,
      EffectiveDate: parseInt(
        moment()
          .year(now.getFullYear())
          .month(now.getMonth() + 1)
          .date(1)
          .format('YYYYMMDD'), 10),
      FirstPayDate: parseInt(
        moment()
          .year(now.getFullYear())
          .month(now.getMonth() + 1)
          .date(25)
          .format('YYYYMMDD'), 10),
      MaturityDate: parseInt(
        moment()
          .year(now.getFullYear() + 12)
          .month(now.getMonth() + 6)
          .date(31)
          .format('YYYYMMDD'), 10),
      EarlyRedemptionDate: parseInt(
        moment()
          .year(now.getFullYear() + 5)
          .date(31)
          .format('YYYYMMDD'), 10),
      CancellationPct: 10,
      LiabilityStepDowns: mDealCirt.DefaultLiabilityStepDownList()
    });
  };

  constructor(rawModelFields = {}) {
    super(rawModelFields);
    const ats = this.ats;
    this.ats = {
      ...mDealCirt.Defaults(),
      Name: null,
      LiabilityStepDowns: []
    };
    this.setRawFields({
      ...this.ats,
      ...rawModelFields
    });
  }

  setRawFields(rawModelFields = {}) {
    for (let prop of Object.keys(rawModelFields)) {
      if (this.ats.hasOwnProperty(prop)) {
        switch (prop) {
          case 'LiabilityStepDowns':
            this.ats[prop] = [];
            for (let [i, obj] of Object.entries(rawModelFields[prop])) {
              this.ats[prop].push(new mDealCirtLiabilityStepDown(obj));
            }
            break;
          default: {
            const value = rawModelFields[prop];
            if (prop.endsWith('Date') && (value != null)) {
              this.ats[prop] = moment(value, 'YYYYMMDD').toDate();
            } else if (typeof value === 'number') {
              this.ats[prop] = formatter.formatAsNumber(value, { minSignsAfterDot: 2, maxSignsAfterDot: 8 });
            } else {
              this.ats[prop] = value;
            }
          }
        }
      }
    }
    return this;
  }

  getRawFieldsFormattedForBackEnd() {
    const ats                = this.getRawFields();
    //ats.Name                 = null;
    ats.ReferencePoolBalance = UtilsData.castToNumber(ats.ReferencePoolBalance, 8);
    ats.Wac                  = UtilsData.castToNumber(ats.Wac, 8);
    ats.Wam                  = UtilsData.castToNumber(ats.Wam, 8);
    ats.AnnualPremiumRatePct = UtilsData.castToNumber(ats.AnnualPremiumRatePct, 8);
    ats.RetentionPct         = UtilsData.castToNumber(ats.RetentionPct, 8);
    ats.LimitOfLiabilityPct  = UtilsData.castToNumber(ats.LimitOfLiabilityPct, 8);
    ats.EffectiveDate        = formatter.formatAsYYYYMMDD(ats.EffectiveDate);
    ats.FirstPayDate         = formatter.formatAsYYYYMMDD(ats.FirstPayDate);
    ats.MaturityDate         = formatter.formatAsYYYYMMDD(ats.MaturityDate);
    ats.EarlyRedemptionDate  = formatter.formatAsYYYYMMDD(ats.EarlyRedemptionDate);
    ats.CancellationPct      = UtilsData.castToNumber(ats.CancellationPct, 8);
    for (let [i, obj] of Object.entries(ats.LiabilityStepDowns)) {
      obj.StartPeriod          = UtilsData.castToNumber(obj.StartPeriod);
      obj.EndPeriod            = UtilsData.castToNumber(obj.EndPeriod);
      //obj.Reassess             = 'Monthly';
      obj.BalanceMultiplier    = UtilsData.castToNumber(obj.BalanceMultiplier, 8);
      obj.DelinquentMultiplier = UtilsData.castToNumber(obj.DelinquentMultiplier, 8);
    }
    return ats;
  }

  // ##################################################
  // region Static Data
  static vocDbToLabel(dbName = null) {
    const ats = {};
    // ##################################################
    // region UI fields from Create CIRT page
    ats.Name                 = 'Deal Name'
    ats.ReferencePoolBalance = 'Reference Pool Balance'; // Unpaid Principal Balance (provided)
    ats.Wac                  = 'WAC';
    ats.Wam                  = 'WAM';
    ats.AnnualPremiumRatePct = 'Annual Premium Rate%';
    ats.RetentionPct         = 'Retention %';
    ats.LimitOfLiabilityPct  = 'Limit of Liability %';
    ats.EffectiveDate        = 'Effective Date';
    ats.FirstPayDate         = 'First Pay Date';
    ats.MaturityDate         = 'Maturity Date';
    ats.EarlyRedemptionDate  = 'Early Redemption Date';
    ats.CancellationPct      = 'Cancellation Percent';
    // endregion UI fields from Create CIRT page
    // ##################################################
    // region Fields from CIRT Analysis Page
    // JFI: These fields discovered on stand-alone page CIRT Analysis and left for example needs.
    // ats.Bonds                     = 'Bonds';
    // ats.SettlementDate            = 'Settlement Date';
    // ats.FirstProjectedDate        = 'First Projected Date';
    // ats.FirstPaymentDate          = 'First Payment Date';
    // ats.MonthsToMaturity          = 'Months To Maturity';
    // ats.CollateralOriginalBalance = 'Collateral Original Balance';
    // ats.CollateralCurrentBalance  = 'Collateral Current Balance';
    // ats.CollateralLosses          = 'Collateral Losses';
    // ats.Wac                       = 'Wac';
    // ats.Wam                       = 'Wam';
    // ats.Wala                      = 'Wala';
    // ats.EarlyRedemptionPct        = 'Early Redemption Pct';
    // ats.EarlyRedemptionDate       = 'Early Redemption Date';
    // ats.DatesOfAvailableUpdates   = 'Dates Of AvailableUpdates';
    // ats.OptionalCancellationDate  = 'Optional Cancellation Date';
    // ats.ParentDealName            = 'Parent DealName';
    // ats.DealName                  = 'Deal Name';
    // ats.IssueDate                 = 'Issue Date';
    // ats.OriginalBalance           = 'Original Balance';
    // ats.CurrentBalance            = 'Current Balance';
    // ats.MaturityDate              = 'Maturity Date';
    // ats.FactorDate                = 'Factor Date';
    // ats.DealFactor                = 'Deal Factor';
    // endregion Fields from CIRT Analysis Page
    // ##################################################
    if (dbName) {
      return ats?.[dbName];
    }
    return ats;
  }

  // endregion Static Data
  // ##################################################
  // region CRUD
  // endregion CRUD
  // ##################################################
  // region Validation
  validate() {
    const ats                    = this.getRawFieldsFormattedForBackEnd();
    this._localValidationStatus  = true;
    this._localValidationResults = [];
    const modelName              = `CIRT`;
    let fieldSysName             = '';
    let fieldName                = '';
    let msg                      = '';
    let fValue                   = null;
    let oErr                     = {};
    const valEffectiveDate       = parseInt(ats.EffectiveDate);
    const valFirstPayDate        = parseInt(ats.FirstPayDate);
    const valEarlyRedemptionDate = parseInt(ats.EarlyRedemptionDate);
    const valMaturityDate        = parseInt(ats.MaturityDate);
    let lt                       = '';
    // ############################################################
    for (let [fieldSysName, fValue] of Object.entries(ats)) {
      fieldName = mDealCirt.vocDbToLabel(fieldSysName)
      // ############################################################
      // Common rules
      msg  = 'Field is required.';
      oErr = { modelName: modelName, field: fieldName, msg: msg, fieldSysName: fieldSysName };
      if (UtilsData.isEmpty(fValue)) this._localValidationResults.push(oErr);
      msg  = 'Invalid Date.';
      oErr = { modelName: modelName, field: fieldName, msg: msg, fieldSysName: fieldSysName };
      if (fValue === 'Invalid date') this._localValidationResults.push(oErr);
      // ############################################################
      // Individual rules
      switch (fieldSysName) {
        case 'Name':
          break;
        case 'EffectiveDate':
          lt   = mDealCirt.vocDbToLabel('FirstPayDate');
          msg  = `${fieldName} should be less than ${lt}`;
          oErr = { modelName: modelName, field: fieldName, msg: msg, fieldSysName: fieldSysName };
          if (valEffectiveDate >= valFirstPayDate) {
            this._localValidationResults.push(oErr);
          }
          break;
        case 'FirstPayDate':
          lt   = mDealCirt.vocDbToLabel('MaturityDate');
          msg  = `${fieldName} should be less than ${lt}`;
          oErr = { modelName: modelName, field: fieldName, msg: msg, fieldSysName: fieldSysName };
          if (valFirstPayDate >= valMaturityDate) {
            this._localValidationResults.push(oErr);
          }
          break;
        case 'EarlyRedemptionDate':
          lt   = mDealCirt.vocDbToLabel('MaturityDate');
          msg  = `${fieldName} should be less than ${lt}`;
          oErr = { modelName: modelName, field: fieldName, msg: msg, fieldSysName: fieldSysName };
          if (valEarlyRedemptionDate >= valMaturityDate) {
            this._localValidationResults.push(oErr);
          }
          break;
        case 'MaturityDate':
          break;
      }
    }
    // ############################################################
    this._localValidationStatus = this._localValidationResults.length === 0;
    return this._localValidationStatus;
  }

  // endregion Validation
  // ##################################################
}
